import { forwardRef, useRef } from 'react';
import { ScrollView, View } from 'react-native';

import { Button, HStack, Text, VStack, theme } from '@squire/ui';

import { PencilIcon } from '@/components/_icons';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { PriceRangeTooltip } from '@/components/booking/PriceRangeTooltip';
import { ViewOrderBottomSheetActions } from '@/components/booking/ViewOrderBottomSheetActions';
import { useOrderSheetMotion } from '@/hooks/booking/useOrderSheetMotion';
import { useIsCategoryFirstEnabled } from '@/hooks/flags/useIsCategoryFirstEnabled';
import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { useDepositDueToday } from '@/hooks/payments/useDepositDueToday';
import { useIsPresentedOverModal } from '@/hooks/shared/useIsPresentedOverModal';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import type { Tip } from '@/types/booking';
import {
  canTipCartItem,
  cartHasAnyProfessionalPriceRange,
  getCartRangePrice,
  getCartTotal,
} from '@/utils/bookingCartUtils';
import composeRefs from '@/utils/composeRefs';
import { formatPrice } from '@/utils/internationalizationUtils';

import { AddAppointmentButtonCard } from '../AddAppointmentButtonCard';
import { EditOrderBottomSheet } from '../EditOrderBottomSheet';
import { ViewOrderCartItem } from '../ViewOrderCartItem';
import { ViewOrderCartPromo } from '../ViewOrderCartPromo';
import { ViewOrderDepositCard } from '../ViewOrderDepositCard';
import { styles } from './styles';
import type { ViewOrderBottomSheetProps } from './types';

const ViewOrderBottomSheet = forwardRef<
  BottomSheetRef,
  ViewOrderBottomSheetProps
>(
  (
    {
      animatedIndex,
      onDismiss,
      onAddAppointment,
      onSelectTip,
      stage = 'choose-service',
      isCreatingCart = false,
      cart,
      shop,
      onConfirmBooking,
      onChooseTime,
      waitlistSelectedIds,
      isStandbyWaitlist = false,
      waitlistTimePreferences,
      onRemoveAddon,
      onCommitEdit,
    },
    ref,
  ) => {
    const bottomSheetRef = useRef<BottomSheetRef>(null);
    const editOrderSheetRef = useRef<BottomSheetRef>(null);

    // A full-height sheet needs the top inset only when the screen behind it
    // owns the status bar area. Presented over a modal, that space is already
    // inset by the modal container.
    const isPresentedOverModal = useIsPresentedOverModal();

    const animationConfigs = useOrderSheetMotion();

    const { t, i18n } = useScreenTranslations('ViewOrderBottomSheet');
    const isEditCartEnabled = useLaunchDarklyFlag(
      'expo-flagship-edit-cart',
      false,
    );
    const currency = cart?.currency || shop?.brand?.currency || 'USD';
    const appointments = cart?.items || [];

    const allAppointmentsHaveSelectedTimes =
      appointments.every(
        (appointment) =>
          appointment.dateTime || waitlistSelectedIds?.has(appointment.id),
      ) && appointments.length > 0;

    const canAddGuest = appointments.length < 5;

    const canTip =
      stage === 'choose-time' && appointments.length > 0 && !isStandbyWaitlist;

    const countTipInPrice = stage === 'choose-time' && !isStandbyWaitlist;

    const showMaxHeight =
      stage === 'choose-time' && allAppointmentsHaveSelectedTimes;

    const singleAppointment =
      appointments.length === 1 ? appointments[0] : undefined;
    const canEditOrder =
      isEditCartEnabled &&
      stage === 'choose-time' &&
      !!singleAppointment &&
      !!singleAppointment.dateTime;

    const cartTotal = getCartTotal(cart, countTipInPrice);
    const rangePrice = getCartRangePrice(cart);
    const isCategoryFirstEnabled = useIsCategoryFirstEnabled(shop ?? {});
    const showSubtotalPriceRangeTooltip =
      isCategoryFirstEnabled && cartHasAnyProfessionalPriceRange(cart);
    const deposit = useDepositDueToday({ shop, cart });

    const dismissBottomSheet = () => {
      bottomSheetRef.current?.close();
    };

    const handleSelectTip =
      ({ barberId }: { barberId: string | null }) =>
      (tip?: Tip | { amount: number; isCustom: true }) => {
        onSelectTip?.(barberId ?? '', tip);
      };

    return (
      <>
        <BottomSheet
          accessibilityLabel={t('title')}
          hideHandle
          hideBackdrop
          testID="view-order-bottom-sheet"
          ref={composeRefs(ref, bottomSheetRef)}
          onDismiss={onDismiss}
          animatedIndex={animatedIndex}
          animationConfigs={animationConfigs}
          dismissible={false}
          closeOnEscape
          style={styles.bottomSheet}
          containerStyle={styles.bottomSheetContentContainer}
          snapPoints={[
            {
              percentage: showMaxHeight ? '100%' : '90%',
            },
          ]}
        >
          <VStack
            flex
            space={theme.spacing[3]}
            paddingHorizontal={theme.spacing[3]}
            style={styles.bottomSheetContent(
              showMaxHeight && !isPresentedOverModal,
            )}
          >
            <HStack style={styles.header} paddingHorizontal={theme.spacing[3]}>
              <VStack flex>
                <Text variant="2xlStrong" color="cartTextPrimary">
                  {t('title')}
                </Text>
                <Text color="cartTextSecondary">{shop?.name}</Text>
              </VStack>

              <View style={styles.headerActions}>
                {canEditOrder && (
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => editOrderSheetRef.current?.open()}
                    accessibilityRole="button"
                    accessibilityLabel={t('accessibility.editOrder')}
                    style={styles.headerAction}
                  >
                    <PencilIcon color="cartIconPrimary" />
                  </Button>
                )}
                <CloseButton
                  inverse
                  iconColor="cartIconPrimary"
                  onPress={dismissBottomSheet}
                  accessibilityLabel={t('accessibility.closeOrderSummary')}
                  accessibilityHint={t('accessibility.closeOrderSummaryHint')}
                  style={styles.headerAction}
                />
              </View>
            </HStack>

            <ScrollView
              bounces={false}
              style={styles.cartItemsScrollView}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.cartItemsScrollViewContent}
            >
              {cart?.items.map((cartItem, index, arr) => {
                const hasTime =
                  !!cartItem.dateTime ||
                  !!waitlistSelectedIds?.has(cartItem.id);
                const isFirstForBarber =
                  arr.findIndex((i) => i.barberId === cartItem.barberId) ===
                  index;
                const isSameBarberGroup =
                  arr.filter((i) => i.barberId === cartItem.barberId).length >
                  1;
                // Per item, so one tip-disabled barber in a group booking
                // doesn't suppress tipping for the others.
                const barberAllowsTips = canTipCartItem(cartItem);

                return (
                  <ViewOrderCartItem
                    key={cartItem.id}
                    cartItem={cartItem}
                    shopTimezone={shop?.timezone}
                    canAddTip={
                      canTip && barberAllowsTips && hasTime && isFirstForBarber
                    }
                    showTipLine={
                      canTip && barberAllowsTips && hasTime && !isFirstForBarber
                    }
                    isSameBarberGroup={isSameBarberGroup}
                    showPriceRangeTooltip={isCategoryFirstEnabled}
                    onSelectTip={handleSelectTip(cartItem)}
                    loading={isCreatingCart}
                    selectedTipAmount={appointments.at(index)?.tipAmount}
                    countTipInPrice={
                      countTipInPrice && !!appointments.at(index)?.tipAmount
                    }
                    currency={currency}
                    waitlistDate={waitlistSelectedIds?.get(cartItem.id)}
                    isStandbyWaitlist={isStandbyWaitlist}
                    waitlistTimePreferences={waitlistTimePreferences?.get(
                      cartItem.id,
                    )}
                    depositCard={
                      deposit && index === arr.length - 1 ? (
                        <ViewOrderDepositCard
                          total={deposit.total}
                          currency={currency}
                          refundable={deposit.refundable}
                        />
                      ) : undefined
                    }
                  />
                );
              })}

              <ViewOrderCartPromo
                promo={cart?.promo}
                currency={cart?.currency}
                timezone={shop?.timezone ?? 'America/New_York'}
              />

              {stage === 'choose-service' &&
                shop?.settings?.groupAppointmentsEnabled &&
                !!appointments.at(-1)?.services.length &&
                appointments.every((item) => !item.bookedWithAnyBarber) && (
                  <AddAppointmentButtonCard
                    canAddGuest={canAddGuest}
                    onAddAppointment={onAddAppointment}
                    testID="add-appointment-button-card"
                  />
                )}
            </ScrollView>

            <VStack space={theme.spacing[3]}>
              {cart && deposit && (
                <HStack
                  fullWidth
                  style={styles.spaceBetween}
                  testID="view-order-deposit-due-today"
                >
                  <Text variant="mStrong" color="cartTextPrimary">
                    {t('depositDueToday')}
                  </Text>
                  <Text variant="mStrong" color="cartTextPrimary">
                    {formatPrice(deposit.total / 100, {
                      locale: i18n.language,
                      currency,
                    })}
                  </Text>
                </HStack>
              )}

              {cart && (
                <HStack fullWidth style={styles.spaceBetween}>
                  <View
                    testID="view-order-subtotal-label"
                    style={styles.subtotalLabel}
                  >
                    <Text variant="2xlStrong" color="cartTextPrimary">
                      {t('subtotal')}
                    </Text>

                    {showSubtotalPriceRangeTooltip && (
                      <PriceRangeTooltip
                        variant="cart"
                        testID="view-order-subtotal-price-range-tooltip"
                      />
                    )}
                  </View>
                  <Text variant="2xlStrong" color="cartTextPrimary">
                    {rangePrice
                      ? `${formatPrice(rangePrice.min / 100, { locale: i18n.language, currency })} - ${formatPrice(rangePrice.max / 100, { locale: i18n.language, currency })}`
                      : formatPrice(cartTotal / 100, {
                          locale: i18n.language,
                          currency,
                        })}
                  </Text>
                </HStack>
              )}

              <ViewOrderBottomSheetActions
                cart={cart}
                shop={shop}
                stage={stage}
                dismissBottomSheet={dismissBottomSheet}
                isCreatingCart={isCreatingCart}
                onContinueToBookingConsent={onConfirmBooking}
                onChooseTime={onChooseTime}
                waitlistSelectedIds={waitlistSelectedIds}
                isStandbyWaitlist={isStandbyWaitlist}
              />
            </VStack>
          </VStack>
        </BottomSheet>

        {canEditOrder && (
          <EditOrderBottomSheet
            ref={editOrderSheetRef}
            cart={cart}
            shop={shop}
            onRemoveAddon={onRemoveAddon}
            onDone={onCommitEdit}
            onCloseOrder={dismissBottomSheet}
          />
        )}
      </>
    );
  },
);

ViewOrderBottomSheet.displayName = 'ViewOrderBottomSheet';
export { ViewOrderBottomSheet };
